import { z } from 'zod';
import { AREA_CODE_PATTERN, ASSET_TYPE_CODES, ASSET_TYPES, type AssetTypeCode } from './constants';
import type { LngLat } from './routes';
import { ROUTE_CATEGORIES, ROUTE_STATUSES } from './routes';

/** Import / export (PRD §5.6). */

export const IMPORT_MAX_BYTES = 20 * 1024 * 1024;
export const IMPORT_MAX_PLACEMARKS = 20_000;

export interface ImportPlacemark {
  i: number;
  name: string;
  /** Folder path joined with " / "; "" for placemarks outside any folder. */
  folder: string;
  kind: 'point' | 'line';
  coords: LngLat | LngLat[];
  description: string | null;
  /** Type guessed from the placemark name (null = none). */
  suggested: AssetTypeCode | null;
  /** Lines: colour from the KML line style as #rrggbb (null = none). */
  color?: string | null;
}

export interface ImportFolder {
  path: string;
  points: number;
  lines: number;
  /** Guess from the folder name, else the most common placemark guess. */
  suggested: AssetTypeCode | null;
}

export interface ImportPreview {
  id: string;
  fileName: string;
  placemarks: ImportPlacemark[];
  folders: ImportFolder[];
  /** Geometry we do not import (polygons, tracks). */
  unsupported: number;
}

/** Folder mapping value: an asset type, `auto` (per-placemark guess, else the default type) or `skip`. */
export const folderTargetSchema = z.union([
  z.enum(ASSET_TYPE_CODES as [string, ...string[]]),
  z.literal('auto'),
  z.literal('skip'),
]);

export const importCommitSchema = z.object({
  defaultType: z.enum(ASSET_TYPE_CODES as [string, ...string[]]).default('ODP'),
  folderTypes: z.record(folderTargetSchema).default({}),
  /** Kept as typed (a trailing space separates the prefix from the name). */
  namePrefix: z
    .string()
    .max(30)
    .transform((v) => v.trimStart())
    .default(''),
  status: z.enum(['planned', 'active']).default('active'),
  /** `null` = from the drawn area boundaries. */
  area: z.string().regex(AREA_CODE_PATTERN, 'Kode wilayah tidak valid').nullable().default(null),
  /** Used when a point lies outside every boundary; `null` = skip such points. */
  fallbackArea: z.string().regex(AREA_CODE_PATTERN, 'Kode wilayah tidak valid').nullable().default(null),
  duplicate: z.enum(['skip', 'overwrite', 'new']).default('skip'),
  matchBy: z.enum(['name', 'proximity']).default('name'),
  proximityM: z.number().min(1).max(100).default(10),
  importLines: z.boolean().default(true),
  lineSnapM: z.number().min(1).max(200).default(30),
  lineStatus: z.enum(ROUTE_STATUSES).default('installed'),
  lineCategory: z.enum(ROUTE_CATEGORIES).default('distribution'),
  /** Line ends with no asset within `lineSnapM`: skip the line, or put a joint closure (JC) there. */
  lineDanglingEnds: z.enum(['skip', 'jc']).default('skip'),
});
export type ImportCommitInput = z.infer<typeof importCommitSchema>;

export interface ImportReport {
  assetsCreated: number;
  assetsUpdated: number;
  routesCreated: number;
  byType: Record<string, number>;
  skipped: Array<{ name: string; folder: string; reason: string }>;
  /** More skipped rows exist than listed. */
  skippedTotal: number;
}

export type ImportStatus =
  | { state: 'running'; done: number; total: number }
  | { state: 'done'; report: ImportReport }
  | { state: 'failed'; error: string };

export const EXPORT_ENTITIES = ['assets', 'routes', 'customers'] as const;
export type ExportEntity = (typeof EXPORT_ENTITIES)[number];

const KEYWORDS: Array<[AssetTypeCode, RegExp]> = [
  ['OLT', /\bolt\b/i],
  ['ODC', /\bodc\b|\bfdt\b|\bodf\b|\bcabinet\b/i],
  ['ODP', /\bodp\b|\bfat\b|\bdistribution point\b/i],
  ['JC', /\bjc\b|\bjb\b|closure|sambungan|\bsplice\b/i],
  ['POLE', /\btiang\b|\bpole\b|\btt\b|\btt[-_ ]?\d/i],
  ['CPE', /pelanggan|customer|\bont\b|\bonu\b|\bhtb\b|\bcpe\b|rumah|\bplg\b/i],
  ['SERVER', /router|server|mikrotik|\brb\d|\bccr\b|switch/i],
  ['AP', /\bap\b|access ?point|wifi|hotspot/i],
];

/** Guess an asset type from a KML folder or placemark name (also the type names FTTH NMS exports). */
export function suggestAssetType(text: string | null | undefined): AssetTypeCode | null {
  if (!text) return null;
  const exact = ASSET_TYPES.find(
    (t) => t.name.toLowerCase() === text.trim().toLowerCase() || t.code === text.trim().toUpperCase(),
  );
  if (exact) return exact.code;
  for (const [code, re] of KEYWORDS) if (re.test(text)) return code;
  return null;
}
